import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from './hooks/useWebSocket';
import { useChat } from './hooks/useChat';
import { ChatPanel } from './components/ChatPanel';
import { ConnectForm } from './components/ConnectForm';
import { useTranslation } from 'react-i18next';
import { initI18n } from './i18n';
import type { ServerMessage, ErrorMessage } from './types/protocol';
import type { ClientMessage } from './types/protocol';
import './styles/singlepilot.css';

export type ThemeMode = 'auto' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';

export interface AppProps {
  serverUrl?: string;
  mode?: 'fullpage' | 'drawer' | 'inline';
  triggerMode?: 'fab' | 'manual';
  /** Color theme: 'light', 'dark', or 'auto' (follows host/system preference) */
  theme?: ThemeMode;
  /** Enable dev inspector panel for raw WebSocket traffic */
  devMode?: boolean;
  clusters?: Array<{ id: string; label: string }>;
  context?: {
    cluster?: string;
    userId?: number;
    userLevel?: number;
    module?: string;
    locale?: string;
    service?: string;
    mcpUrl?: string;
    userInfoUrl?: string;
    userToken?: string;
  };
  onReady?: () => void;
  onDrawerOpen?: () => void;
  onDrawerClose?: () => void;
  onDrawerApiReady?: (api: { open: () => void; close: () => void; toggle: () => void }) => void;
  onTokenRefresh?: () => Promise<string>;
}

type ContextParams = {
  cluster?: string;
  userId?: number;
  userLevel?: number;
  module?: string;
  locale?: string;
  service?: string;
  mcpUrl?: string;
  userInfoUrl?: string;
  userToken?: string;
};

const DEFAULT_CLUSTERS = [
  { id: 'qa', label: 'QA' },
];

function getHostTheme(): ResolvedTheme {
  if (typeof document !== 'undefined') {
    if (
      document.body.classList.contains('dark-mode') ||
      document.documentElement.classList.contains('dark') ||
      document.documentElement.classList.contains('dark-mode')
    ) {
      return 'dark';
    }
  }

  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
}

export function App({
  serverUrl: initialUrl,
  mode = 'fullpage',
  triggerMode = 'fab',
  theme = 'auto',
  devMode = false,
  clusters = DEFAULT_CLUSTERS,
  context: autoContext,
  onReady,
  onDrawerOpen,
  onDrawerClose,
  onDrawerApiReady,
  onTokenRefresh,
}: AppProps): React.ReactElement {
  // Initialize i18n with locale from context
  initI18n(autoContext?.locale);
  const { t } = useTranslation();

  const chat = useChat(devMode);
  const setConnectionParams = chat.setConnectionParams;
  // Restore connection params from previous session (fullpage mode without autoContext)
  const savedSession = !autoContext ? chat.getSavedSession() : null;
  const savedParams = savedSession?.connectionParams;
  const [wsUrl, setWsUrl] = useState(initialUrl ?? savedParams?.wsUrl ?? 'ws://localhost:8082/ws');
  const [shouldConnect, setShouldConnect] = useState(!!autoContext || !!savedParams);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hostTheme, setHostTheme] = useState<ResolvedTheme>(() => getHostTheme());
  const restoredRef = useRef(false);
  const pendingContextRef = useRef(savedParams ?? null);
  const drawerOpenRef = useRef(false);

  const resolvedTheme: ResolvedTheme = theme === 'auto' ? hostTheme : theme;
  const themeClass = resolvedTheme === 'dark' ? ' sp-dark' : '';

  // ACDDEV-2049: Track current userToken in a ref so that reconnects use the
  // latest refreshed token instead of the stale one baked at page load.
  const userTokenRef = useRef(autoContext?.userToken);
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsSendRef = useRef<(msg: ClientMessage) => void>(() => {});
  const lastContextRef = useRef<ContextParams | null>(null);

  const getAutoContextParams = useCallback((token?: string): ContextParams | null => {
    if (!autoContext) return null;
    return {
      cluster: autoContext.cluster,
      userId: autoContext.userId,
      userLevel: autoContext.userLevel,
      module: autoContext.module,
      locale: autoContext.locale,
      service: autoContext.service,
      mcpUrl: autoContext.mcpUrl,
      userInfoUrl: autoContext.userInfoUrl,
      userToken: token ?? userTokenRef.current,
    };
  }, [autoContext]);

  /**
   * Decode the token's expiry (seconds since epoch) from the base64 payload.
   * Token format: base64(JSON{userId, cluster, exp}).signature or :signature.
   */
  const getTokenExpiry = useCallback((token: string): number | null => {
    const dot = token.indexOf('.');
    const colon = token.indexOf(':');
    const separator = dot < 0 ? colon : colon < 0 ? dot : Math.min(dot, colon);
    if (separator < 1) return null;
    try {
      const encoded = token.substring(0, separator).replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Schedule a proactive token refresh 5 minutes before expiry.
   * Clears any existing timer first.
   */
  const scheduleTokenRefresh = useCallback((token: string) => {
    if (!onTokenRefresh) return;
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
    const exp = getTokenExpiry(token);
    if (exp === null) return;

    // Refresh 5 minutes before expiry, minimum 10 seconds from now
    const msUntilRefresh = Math.max((exp - 300) * 1000 - Date.now(), 10_000);
    tokenRefreshTimerRef.current = setTimeout(() => {
      tokenRefreshTimerRef.current = null;
      onTokenRefresh().then((newToken) => {
        if (typeof newToken !== 'string' || !newToken) {
          throw new Error('Token refresh returned empty value');
        }
        userTokenRef.current = newToken;
        // Re-authenticate the session with the fresh token
        const currentContext = lastContextRef.current ?? getAutoContextParams();
        if (currentContext) {
          const refreshedContext = { ...currentContext, userToken: newToken };
          lastContextRef.current = refreshedContext;
          wsSendRef.current({ type: 'set_context', ...refreshedContext });
        }
        // Schedule the next refresh based on the new token's expiry
        scheduleTokenRefresh(newToken);
      }).catch(() => {
        // Token refresh failed (e.g., PHP session expired) — retry in 60s
        tokenRefreshTimerRef.current = setTimeout(() => {
          const currentToken = userTokenRef.current;
          if (currentToken) scheduleTokenRefresh(currentToken);
        }, 60_000);
      });
    }, msUntilRefresh);
  }, [onTokenRefresh, getAutoContextParams, getTokenExpiry]);

  // Start the refresh timer on mount if we have a token
  useEffect(() => {
    if (userTokenRef.current) {
      scheduleTokenRefresh(userTokenRef.current);
    }
    return () => {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  // Fallback: if the server returns UNAUTHENTICATED despite proactive refresh
  // (e.g. clock skew, secret rotation), attempt one immediate refresh.
  // The flag prevents looping if the fresh token is also rejected.
  const reactiveRefreshAttemptedRef = useRef(false);
  const handleServerMessage = useCallback((message: ServerMessage) => {
    if (
      message.type === 'error' &&
      (message as ErrorMessage).code === 'UNAUTHENTICATED' &&
      onTokenRefresh &&
      !reactiveRefreshAttemptedRef.current
    ) {
      reactiveRefreshAttemptedRef.current = true;
      onTokenRefresh().then((newToken) => {
        if (typeof newToken !== 'string' || !newToken) {
          throw new Error('Token refresh returned empty value');
        }
        userTokenRef.current = newToken;
        reactiveRefreshAttemptedRef.current = false;
        const currentContext = lastContextRef.current ?? getAutoContextParams(newToken);
        if (currentContext) {
          const refreshedContext = { ...currentContext, userToken: newToken };
          lastContextRef.current = refreshedContext;
          wsSendRef.current({ type: 'set_context', ...refreshedContext });
        }
        scheduleTokenRefresh(newToken);
      }).catch(() => {
        reactiveRefreshAttemptedRef.current = false;
        // Token refresh failed — surface the error to the user
        chat.handleServerMessage(message);
      });
      return;
    }
    // If reactive refresh was already attempted and failed, show the error
    if (
      message.type === 'error' &&
      (message as ErrorMessage).code === 'UNAUTHENTICATED'
    ) {
      reactiveRefreshAttemptedRef.current = false;
    }
    chat.handleServerMessage(message);
  }, [onTokenRefresh, chat, scheduleTokenRefresh, getAutoContextParams]);

  const ws = useWebSocket({
    url: wsUrl,
    onMessage: handleServerMessage,
    autoConnect: shouldConnect,
  });
  wsSendRef.current = ws.send;

  const wsSend = useCallback((msg: ClientMessage) => {
    ws.send(msg);
  }, [ws]);

  const sendSetContext = useCallback((params: ContextParams) => {
    const contextParams = {
      cluster: params.cluster,
      userId: params.userId,
      userLevel: params.userLevel,
      module: params.module,
      locale: params.locale,
      service: params.service,
      mcpUrl: params.mcpUrl,
      userInfoUrl: params.userInfoUrl,
      userToken: params.userToken,
    };
    lastContextRef.current = contextParams;
    wsSend({ type: 'set_context', ...contextParams });
  }, [wsSend]);

  // Call onReady on mount + hide Beamer in fullpage mode
  useEffect(() => {
    onReady?.();
    if (mode === 'fullpage') {
      // Beamer loads async — observe DOM until it appears, then hide it
      const hide = () => {
        const el = document.getElementById('beamerSelector');
        if (el) { el.style.display = 'none'; return true; }
        return false;
      };
      if (!hide()) {
        const observer = new MutationObserver(() => {
          if (hide()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drawer open/close callbacks + Beamer visibility
  useEffect(() => {
    if (mode !== 'drawer') return;
    // Skip initial render
    if (drawerOpenRef.current === drawerOpen) return;
    drawerOpenRef.current = drawerOpen;
    // Toggle Beamer visibility
    const beamer = document.getElementById('beamerSelector');
    if (beamer) beamer.style.display = drawerOpen ? 'none' : '';
    if (drawerOpen) {
      onDrawerOpen?.();
    } else {
      onDrawerClose?.();
    }
  }, [drawerOpen, mode, onDrawerOpen, onDrawerClose]);

  // Expose drawer API
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((prev) => !prev), []);

  useEffect(() => {
    if (mode === 'drawer' && onDrawerApiReady) {
      onDrawerApiReady({ open: openDrawer, close: closeDrawer, toggle: toggleDrawer });
    }
  }, [mode, onDrawerApiReady, openDrawer, closeDrawer, toggleDrawer]);

  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const syncTheme = () => setHostTheme(getHostTheme());
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    mediaQuery?.addEventListener('change', syncTheme);

    const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver(syncTheme) : null;
    observer?.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    syncTheme();

    return () => {
      mediaQuery?.removeEventListener('change', syncTheme);
      observer?.disconnect();
    };
  }, [theme]);

  // Auto-set context when provided and connected
  useEffect(() => {
    if (chat.connectionStatus === 'connected') {
      if (autoContext) {
        const params = getAutoContextParams();
        if (params) {
          sendSetContext(params);
          if (typeof params.userId === 'number') {
            setConnectionParams({
              wsUrl,
              cluster: params.cluster,
              userId: params.userId,
              userLevel: params.userLevel,
              module: params.module,
              locale: params.locale,
              service: params.service,
              mcpUrl: params.mcpUrl,
              userInfoUrl: params.userInfoUrl,
              userToken: params.userToken,
            });
          }
        }
      } else if (pendingContextRef.current) {
        // Auto-reconnect from saved session
        const params = pendingContextRef.current;
        pendingContextRef.current = null;
        const connectWithParams = (nextParams: typeof params) => {
          if (nextParams.userToken) {
            userTokenRef.current = nextParams.userToken;
            scheduleTokenRefresh(nextParams.userToken);
          }
          setConnectionParams(nextParams);
          sendSetContext(nextParams);
        };

        if (!params.userToken && onTokenRefresh) {
          onTokenRefresh().then((newToken) => {
            if (typeof newToken !== 'string' || !newToken) {
              throw new Error('Token refresh returned empty value');
            }
            connectWithParams({ ...params, userToken: newToken });
          }).catch(() => {
            connectWithParams(params);
          });
        } else {
          connectWithParams(params);
        }
      }
    }
  }, [
    autoContext,
    chat.connectionStatus,
    getAutoContextParams,
    onTokenRefresh,
    scheduleTokenRefresh,
    sendSetContext,
    setConnectionParams,
    wsUrl,
  ]);

  // Restore workflow session after context is set
  useEffect(() => {
    if (chat.connectionStatus === 'context_set' && !restoredRef.current) {
      restoredRef.current = true;
      const saved = chat.getSavedSession();
      if (saved?.workflowId) {
        wsSend({
          type: 'restore_session',
          workflowId: saved.workflowId,
          answers: saved.answers ?? {},
          flowData: saved.previewData ?? undefined,
        });
      }
    }
  }, [chat.connectionStatus, chat, wsSend]);

  const handleConnect = useCallback(
    (url: string) => {
      setWsUrl(url);
      setShouldConnect(true);
    },
    [],
  );

  const handleSetContext = useCallback(
    (cluster: string, userId: number) => {
      sendSetContext({ cluster, userId });
      setConnectionParams({ wsUrl, cluster, userId });
    },
    [sendSetContext, wsUrl, setConnectionParams],
  );

  const handleNewChat = useCallback(() => {
    chat.clearChat();
    wsSend({ type: 'new_session' });
  }, [chat, wsSend]);

  // Drawer mode: floating button + slide-out panel
  if (mode === 'drawer') {
    const showFab = triggerMode !== 'manual';
    const pd = chat.previewData as Record<string, unknown> | null;
    const nodes = pd && Array.isArray((pd as Record<string, unknown>).nodes) ? (pd as Record<string, unknown>).nodes as unknown[] : [];
    const drawerExpanded = !!(chat.previewConfig?.expandDrawer && nodes.length > 0);
    return (
      <>
        {showFab && (
          <button
            type="button"
            className={`sp-drawer-toggle${drawerOpen ? ' sp-drawer-toggle-hidden' : ''}`}
            onClick={toggleDrawer}
            aria-label={t('app.openSinglePilot')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </button>
        )}
        <div className={`sp-drawer${drawerOpen ? ' sp-drawer-open' : ''}${drawerExpanded ? ' sp-drawer-expanded' : ''}`}>
          <div className={`sp-root sp-mode-drawer${themeClass}`}>
            <header className="sp-header">
              <h1 className="sp-logo">{t('app.name')}</h1>
              {chat.connectionStatus === 'context_set' && chat.workflowName && (
                <span className="sp-workflow-badge">{chat.workflowName}</span>
              )}
              <div className="sp-header-actions">
                {chat.connectionStatus === 'context_set' && (chat.messages.length > 1 || chat.workflowName) && (
                  <button
                    type="button"
                    className="sp-new-chat-btn"
                    onClick={handleNewChat}
                    title={t('app.newChat')}
                    aria-label={t('app.newChat')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>{t('app.newChat')}</span>
                  </button>
                )}
                {chat.sessionId && (
                  <span className="sp-session-id" title={chat.sessionId}>
                    {chat.sessionId.slice(0, 8)}
                  </span>
                )}
                <button
                  type="button"
                  className="sp-drawer-close"
                  onClick={toggleDrawer}
                  aria-label={t('app.close')}
                >
                  &times;
                </button>
              </div>
            </header>

            {chat.connectionStatus !== 'context_set' ? (
              <div className="sp-connecting">
                <div className="sp-connecting-spinner" />
                <p>{ws.status === 'error' ? t('app.connectionFailed') : t('app.connecting')}</p>
              </div>
            ) : (
              <ChatPanel chat={chat} ws={ws} mode="drawer" locale={autoContext?.locale} devMode={devMode} />
            )}
          </div>
        </div>
      </>
    );
  }

  // Fullpage / inline mode
  return (
    <div className={`sp-root sp-mode-${mode}${themeClass}`}>
      <header className="sp-header">
        <h1 className="sp-logo">{t('app.name')}</h1>
        {chat.connectionStatus === 'context_set' && chat.workflowName && (
          <span className="sp-workflow-badge">{chat.workflowName}</span>
        )}
        <div className="sp-header-actions">
          {chat.connectionStatus === 'context_set' && (chat.messages.length > 1 || chat.workflowName) && (
            <button
              type="button"
              className="sp-new-chat-btn"
              onClick={handleNewChat}
              title={t('app.newChat')}
              aria-label={t('app.newChat')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>{t('app.newChat')}</span>
            </button>
          )}
          {chat.sessionId && (
            <span className="sp-session-id" title={chat.sessionId}>
              {chat.sessionId.slice(0, 8)}
            </span>
          )}
        </div>
      </header>

      {chat.connectionStatus !== 'context_set' ? (
        autoContext ? (
          <div className="sp-connecting">
            <div className="sp-connecting-spinner" />
            <p>{ws.status === 'error' ? t('app.connectionFailed') : t('app.connecting')}</p>
          </div>
        ) : (
          <ConnectForm
            connectionStatus={chat.connectionStatus}
            wsStatus={ws.status}
            clusters={clusters}
            defaultServerUrl={initialUrl}
            onConnect={handleConnect}
            onSetContext={handleSetContext}
          />
        )
      ) : (
        <ChatPanel chat={chat} ws={ws} locale={autoContext?.locale} devMode={devMode} />
      )}
    </div>
  );
}
